import React, {useEffect, useState, useSyncExternalStore} from "react";
import {TouchInput} from "../../touchInput";
import {HAND_KEYS, HandKey, releaseTouchHandKeys, setTouchHandKey} from "../../match/ballInHand";
import {useGameStore} from "../../features/game/game-store";

// The on-screen part of the Touch input mode (touchControls.ts has the model): the LOCK button (= holding Space),
// the W A S D pad for ball in hand (= the keys), and zoom buttons (= the mouse wheel). Shown only in Touch mode.

const ARROWS: Record<HandKey, {label: string, title: string}> = {
    KeyW: {label: "▲", title: "Move the cue ball away from you (W)"},
    KeyA: {label: "◀", title: "Move the cue ball left (A)"},
    KeyS: {label: "▼", title: "Move the cue ball towards you (S)"},
    KeyD: {label: "▶", title: "Move the cue ball right (D)"},
};

const stop = (e: React.SyntheticEvent) => { e.stopPropagation(); };

function LockButton({active}: {active: boolean}) {
    const locked = useSyncExternalStore(TouchInput.subscribe, () => TouchInput.locked);
    return <button type="button" id="touch-lock" className={"touch-btn touch-lock" + (locked ? " pressed" : "") + (active ? "" : " idle")}
                   aria-pressed={locked} aria-label="Hold to lock the aim (like holding Space)"
                   onPointerDown={e => {
                       stop(e); e.preventDefault();
                       try { (e.target as HTMLElement).setPointerCapture(e.pointerId); } catch {}
                       TouchInput.lockDown(e.pointerId, e.timeStamp || performance.now());
                   }}
                   onPointerUp={e => { stop(e); TouchInput.lockUp(e.pointerId); }}
                   onPointerCancel={e => TouchInput.lockUp(e.pointerId)}
                   onLostPointerCapture={e => TouchInput.lockUp(e.pointerId)}
                   onContextMenu={e => e.preventDefault()}>
        <span className="lock-icon" aria-hidden="true">{locked ? "🔒" : "🔓"}</span>
        <span className="lock-text">{locked ? "LOCKED" : "HOLD"}</span>
    </button>;
}

function HandPad() {
    useEffect(() => () => releaseTouchHandKeys(), []);
    return <div className="touch-pad" id="touch-hand-pad" aria-label="Place the cue ball">
        {HAND_KEYS.map(k => <button key={k} type="button" className={"touch-btn pad-" + k} title={ARROWS[k].title} aria-label={ARROWS[k].title}
                                    onPointerDown={e => { stop(e); e.preventDefault(); try { (e.target as HTMLElement).setPointerCapture(e.pointerId); } catch {} setTouchHandKey(k, true); }}
                                    onPointerUp={() => setTouchHandKey(k, false)}
                                    onPointerCancel={() => setTouchHandKey(k, false)}
                                    onLostPointerCapture={() => setTouchHandKey(k, false)}
                                    onContextMenu={e => e.preventDefault()}>{ARROWS[k].label}</button>)}
    </div>;
}

function usePortrait() {
    const q = "(orientation: portrait)";
    const get = () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(q).matches;
    const [portrait, setPortrait] = useState(get);
    useEffect(() => {
        const update = () => setPortrait(get());
        window.addEventListener("resize", update);
        window.addEventListener("orientationchange", update);
        return () => { window.removeEventListener("resize", update); window.removeEventListener("orientationchange", update); };
    }, []);
    return portrait;
}

export default function TouchControls() {
    const mode = useGameStore(s => s.mode);
    const phase = useGameStore(s => s.phase);
    const status = useGameStore(s => s.status);
    const turn = useGameStore(s => s.turn);
    const you = useGameStore(s => s.you);
    const ballInHand = useGameStore(s => s.ballInHand);
    const portrait = usePortrait();
    const [learned, setLearned] = useState(false);
    const [rotateDismissed, setRotateDismissed] = useState(false);
    useEffect(() => TouchInput.subscribe(() => { if (TouchInput.locked) setLearned(true); }), []);
    // a dialog or a new mode: no finger stays "down"
    useEffect(() => () => TouchInput.cancelAll(), []);

    const myTurn = mode !== "online" || turn === you;
    const aiming = status === "playing" && phase === "aiming" && myTurn;

    return <div className="touch-controls">
        {aiming && !learned ? <div className="touch-hint" id="touch-hint">
            Drag on the table to look and aim. Hold <b>HOLD</b> (or keep one finger down and add a second) to lock the aim,
            then drag down and push up through the ball: the faster the push, the harder the shot.
        </div> : null}
        {portrait && !rotateDismissed ? <div className="touch-rotate" role="note">
            Turn your phone sideways for the best view.
            <button type="button" aria-label="Dismiss" onClick={() => setRotateDismissed(true)}>×</button>
        </div> : null}
        <div className="touch-zoom">
            <button type="button" className="touch-btn" id="touch-zoom-in" aria-label="Zoom in" onClick={() => TouchInput.zoom(-1)}>+</button>
            <button type="button" className="touch-btn" id="touch-zoom-out" aria-label="Zoom out" onClick={() => TouchInput.zoom(1)}>−</button>
        </div>
        {aiming && ballInHand ? <HandPad/> : null}
        <LockButton active={aiming}/>
    </div>;
}
